import * as firebase from 'firebase'
import moment from 'moment'

const RANGES_MIN_DATE = '2000-01-01T00:00:00.000Z'
const RANGES_MAX_DATE = '3000-01-01T00:00:00.000Z'

const QueryBuilder = {
  
  buildCustomerEventsQuery: (teamId, customerId, contactId, from, to) => {
    if (!customerId) {
      return null
    }
    let query = firebase.database().ref(`customer_events/${teamId}`)
    // Single field filter (customerId)
    if (!contactId && !from && !to) {
      query = query.orderByChild('customerId').equalTo(customerId)
    }
    else
    // Two fields filter (customerId and dates)
    if (!contactId && (from || to)) {
      query = query.orderByChild('customerId_endDate')
      if (from) {
        query = query.startAt(`${customerId}@${from}`)
      }
      if (to) {
        query = query.endAt(`${customerId}@${to}`)
      }
    }
    else
    // Two fields filter (customerId and contactId)
    if (contactId && !from && !to) {
      query = query.orderByChild('customerContactId').equalTo(`${contactId}@${customerId}`)
    }
    // Three fields filter (customerId, contactId and dates)
    else {
      query = query.orderByChild('customerContactId_endDate')
      if (from) {
        query = query.startAt(`${contactId}@${customerId}@${from}`)
      }
      if (to) {
        query = query.endAt(`${contactId}@${customerId}@${to}`)
      }
    }
    return query
  },

  hasStatusFilter: filters => filters.status && filters.status !== 'all' && filters.status !== 'unpaid',

  hasUnpaidStatusFilter: filters => filters.status === 'unpaid',

  hasCustomerFilter: filters => filters.customerId && filters.customerId !== 'all',

  hasDateFilters: filters => filters.from || filters.to,

  getStartDateFilterStr: date => {
    return date ? moment(date).format() : RANGES_MIN_DATE
  },

  getEndDateFilterStr: date => {
    return date ? moment(date).add(1, 'days').format() : RANGES_MAX_DATE
  },

  buildInvoicesListQuery: (teamId, filters) => {
    // For simplicity, number query overrides other filters
    let query = firebase.database().ref(`invoices/${teamId}`)
    if (filters.number) {
      query = query.orderByChild('number').equalTo(filters.number)
    }
    else
    // Single field queries (customer)
    if (QueryBuilder.hasCustomerFilter(filters)
      && !QueryBuilder.hasStatusFilter(filters) && !QueryBuilder.hasUnpaidStatusFilter(filters)
      && !QueryBuilder.hasDateFilters(filters)) {
      query = query.orderByChild('customerId').equalTo(filters.customerId)
    }
    else
    // Single field queries (status)
    if (QueryBuilder.hasStatusFilter(filters)
      && !QueryBuilder.hasCustomerFilter(filters)
      && !QueryBuilder.hasDateFilters(filters)) {
      query = query.orderByChild('status').equalTo(filters.status)
    }
    else
    // Single field queries (invoiceDate)
    if (QueryBuilder.hasDateFilters(filters)
      && !QueryBuilder.hasStatusFilter(filters) && !QueryBuilder.hasUnpaidStatusFilter(filters)
      && !QueryBuilder.hasCustomerFilter(filters)) {
      query = query.orderByChild('invoiceDate')
        .startAt(QueryBuilder.getStartDateFilterStr(filters.from))
        .endAt(QueryBuilder.getEndDateFilterStr(filters.to))
    }
    // Two field filters (customer & status)
    else
    if (QueryBuilder.hasCustomerFilter(filters) && QueryBuilder.hasStatusFilter(filters)
      && !QueryBuilder.hasDateFilters(filters)) {
      query = query.orderByChild('customerId_status').equalTo(`${filters.customerId}_${filters.status}`)
    }
    else
    // Two field filters (customer & invoice date)
    if (QueryBuilder.hasCustomerFilter(filters) && QueryBuilder.hasDateFilters(filters)
      && !QueryBuilder.hasStatusFilter(filters) && !QueryBuilder.hasUnpaidStatusFilter(filters)) {
      query = query.orderByChild('customerId_invoiceDate')
        .startAt(`${filters.customerId}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
        .endAt(`${filters.customerId}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
    }
    // Two field filters (status & invoice date)
    else
    if (QueryBuilder.hasStatusFilter(filters) && QueryBuilder.hasDateFilters(filters)
      && !QueryBuilder.hasCustomerFilter(filters)) {
      query = query.orderByChild('status_invoiceDate')
        .startAt(`${filters.status}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
        .endAt(`${filters.status}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
    }
    else
    // Three filters combined
    if (QueryBuilder.hasStatusFilter(filters) && QueryBuilder.hasDateFilters(filters)
      && QueryBuilder.hasCustomerFilter(filters)) {
      query = query.orderByChild('customerId_status_invoiceDate')
        .startAt(`${filters.customerId}_${filters.status}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
        .endAt(`${filters.customerId}_${filters.status}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
    }
    // Check for unpaid status filter and posible combinations
    else {
      // Single filter for unpaid invoices
      if (QueryBuilder.hasUnpaidStatusFilter(filters)
        && !QueryBuilder.hasCustomerFilter(filters)
        && !QueryBuilder.hasDateFilters(filters)) {
        query = query.orderByChild('statusUnpaid').equalTo('true')
      }
      else
      // Single filter for unpaid invoices of a customer
      if (QueryBuilder.hasUnpaidStatusFilter(filters)
        && QueryBuilder.hasCustomerFilter(filters)
        && !QueryBuilder.hasDateFilters(filters)) {
        query = query.orderByChild('statusUnpaid_customerId').equalTo(`true_${filters.customerId}`)
      }
      else
      // Single filter for unpaid invoices on a dates range
      if (QueryBuilder.hasUnpaidStatusFilter(filters)
        && !QueryBuilder.hasCustomerFilter(filters)
        && QueryBuilder.hasDateFilters(filters)) {
        query = query.orderByChild('statusUnpaid_invoiceDate')
          .startAt(`true_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
          .endAt(`true_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
      }
      else
      // Double filter for unpaid invoices of a customer on a dates range
      if (QueryBuilder.hasUnpaidStatusFilter(filters)
        && QueryBuilder.hasCustomerFilter(filters)
        && QueryBuilder.hasDateFilters(filters)) {
        query = query.orderByChild('statusUnpaid_customerId_invoiceDate')
          .startAt(`true_${filters.customerId}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
          .endAt(`true_${filters.customerId}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
      }
    }
    return query
  },

  buildEstimatesListQuery: (teamId, filters) => {
    // For simplicity, number query overrides other filters
    let query = firebase.database().ref(`estimates/${teamId}`)
    if (filters.number) {
      query = query.orderByChild('number').equalTo(filters.number)
    }
    else
    // Single field queries (customer)
    if (QueryBuilder.hasCustomerFilter(filters)
      && !QueryBuilder.hasStatusFilter(filters)
      && !QueryBuilder.hasDateFilters(filters)) {
      query = query.orderByChild('customerId').equalTo(filters.customerId)
    }
    else
    // Single field queries (status)
    if (QueryBuilder.hasStatusFilter(filters)
      && !QueryBuilder.hasCustomerFilter(filters)
      && !QueryBuilder.hasDateFilters(filters)) {
      query = query.orderByChild('status').equalTo(filters.status)
    }
    else
    // Single field queries (invoiceDate)
    if (QueryBuilder.hasDateFilters(filters)
      && !QueryBuilder.hasStatusFilter(filters)
      && !QueryBuilder.hasCustomerFilter(filters)) {
      query = query.orderByChild('estimateDate')
        .startAt(QueryBuilder.getStartDateFilterStr(filters.from))
        .endAt(QueryBuilder.getEndDateFilterStr(filters.to))
    }
    // Two field filters (customer & status)
    else
    if (QueryBuilder.hasCustomerFilter(filters) && QueryBuilder.hasStatusFilter(filters)
      && !QueryBuilder.hasDateFilters(filters)) {
      query = query.orderByChild('customerId_status').equalTo(`${filters.customerId}_${filters.status}`)
    }
    else
    // Two field filters (customer & invoice date)
    if (QueryBuilder.hasCustomerFilter(filters) && QueryBuilder.hasDateFilters(filters)
      && !QueryBuilder.hasStatusFilter(filters)) {
      query = query.orderByChild('customerId_estimateDate')
        .startAt(`${filters.customerId}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
        .endAt(`${filters.customerId}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
    }
    // Two field filters (status & invoice date)
    else
    if (QueryBuilder.hasStatusFilter(filters) && QueryBuilder.hasDateFilters(filters)
      && !QueryBuilder.hasCustomerFilter(filters)) {
      query = query.orderByChild('status_estimateDate')
        .startAt(`${filters.status}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
        .endAt(`${filters.status}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
    }
    else
    // Three filters combined
    if (QueryBuilder.hasStatusFilter(filters) && QueryBuilder.hasDateFilters(filters)
      && QueryBuilder.hasCustomerFilter(filters)) {
      query = query.orderByChild('customerId_status_estimateDate')
        .startAt(`${filters.customerId}_${filters.status}_${QueryBuilder.getStartDateFilterStr(filters.from)}`)
        .endAt(`${filters.customerId}_${filters.status}_${QueryBuilder.getEndDateFilterStr(filters.to)}`)
    }
    return query
  }

}

export default QueryBuilder



// WEBPACK FOOTER //
// ./src/billing/infra/QueryBuilder.js